/**
 * Runtime configuration for the frontend.
 *
 * The SPA ships as ONE environment-agnostic artifact. Values that used to be
 * inlined by Vite at build time (`VITE_*`) are now fetched at boot from
 * `/config.json`, which the container entrypoint writes from environment
 * variables (see `docker/frontend-entrypoint.sh`). That is what makes
 * "promote the tested image" meaningful: staging and production run the same
 * bytes and differ only in the JSON served next to them.
 *
 * Two invariants make this safe:
 *
 * 1. **`config.json` is never cached.** nginx serves it `no-store`
 *    (`infra/nginx-frontend.conf`) and the fetch below asks for `no-store`
 *    too. A stale copy carried between environments would point a browser at
 *    the wrong API and MSAL client.
 * 2. **The `import.meta.env` fallback is load-bearing, not a nicety.** Local
 *    `pnpm dev`, Storybook, Vitest, and the Playwright mock build all run with
 *    no served `config.json`. A 404, a network failure, or an unparseable body
 *    must resolve to "fall back to the build-time env", never throw.
 *
 * Platform-neutral: `fetch` works on every target. No browser globals here.
 *
 * This is deliberately NOT a capability port. Capability ports are injected
 * through React context at app root, but configuration must resolve *before*
 * React mounts. See
 * `docs/features/staging-environment/02_enterprise_architecture_impact_assessment.md`.
 */

/** Resolved configuration for one running frontend instance. */
export interface RuntimeConfig {
  /** Base URL of the API. Empty means mock mode (no backend). */
  apiBaseUrl: string;
  /** `entra` or `dev_bypass`. */
  authMode: string;
  /** Default GitHub repository (`owner/repo`) for feature sessions. */
  githubRepository: string;
  /** SPA client ID (Entra app registration for the frontend). */
  msalClientId: string;
  /** Entra directory tenant ID. Empty falls back to the `/common` authority. */
  msalTenantId: string;
  /** Delegated API scope: `api://<api-app-client-id>/<scope-name>`. */
  apiScope: string;
  /** Interactive redirect URI. Empty means "derive from the current origin". */
  msalRedirectUri: string;
  /** Silent (hidden-iframe) renewal redirect URI. Empty means "derive". */
  msalSilentRedirectUri: string;
  /** Whether Grafana Faro should initialise. */
  faroEnabled: boolean;
  /** Grafana Cloud Frontend Observability collector URL. */
  faroCollectorUrl: string;
  /** Deployment environment name, e.g. `staging`, `production`, `dev`. */
  environment: string;
  /** Release identifier reported on observability signals. */
  appVersion: string;
  /**
   * How the customer portal signs people in: empty for Entra External ID, `simulated` in a testing preview
   * (quote-to-invoice-e2e-testing 07 §2), where the sign-in page sends the tester to the test console instead.
   */
  customerAuthMode: string;
  /**
   * How the supplier portal signs people in: empty for Entra External ID, `simulated` in a testing preview, where
   * the sign-in page sends the tester to the test console instead (as `customerAuthMode`).
   */
  supplierAuthMode: string;
  /**
   * Base URL of the API that keeps quick feedback notes (the hub: production). Empty means this environment's own
   * API, so production itself and local dev send to themselves. Feedback quick capture spec 01 §3.
   */
  feedbackHubApiBaseUrl: string;
  /** Delegated scope of a token for the hub: `api://<hub-api-app-client-id>/feedback.submit`. Empty with no hub. */
  feedbackHubScope: string;
  /**
   * `off` makes mock mode serve only what modules serve, without the registered mock-up drafts
   * (mock-data/object-ui-drafts). Empty shows them. Playwright's build sets `off`; the dev server and mock previews leave it empty.
   */
  objectUiDrafts: string;
}

const RUNTIME_CONFIG_URL = "/config.json";

let cached: RuntimeConfig | undefined;

/**
 * `vite/client` is deliberately not referenced anywhere in the workspace (see
 * `apps/frontend/src/css.d.ts`), so the build environment's ambient shape is
 * declared here and travels with the import into every consuming tsconfig.
 * Optional because a non-Vite runtime has no `import.meta.env` at all.
 */
declare global {
  interface ImportMeta {
    readonly env?: Record<string, string | undefined>;
  }
}

function trimmed(raw: string | undefined): string {
  return (raw ?? "").trim();
}

/**
 * The build-time fallback. Also supplies the defaults that a served
 * `config.json` is overlaid onto, so a partial file is still usable.
 *
 * Every read is a *static* `import.meta.env.VITE_…` access, and that shape is
 * load-bearing rather than stylistic. Vite substitutes the static form per key;
 * a bare `import.meta.env` reference is replaced wholesale by a frozen snapshot
 * of whatever `.env` the bundler loaded at config time, which nothing can
 * intercept afterwards. The snapshot form silently read the developer's own
 * `apps/frontend/.env` under Vitest — `vi.stubEnv` reaches the static form and
 * cannot reach the snapshot, so the tests could not pin the build environment
 * and failed on any machine that had followed the `.env.example` setup.
 *
 * The optional chaining is what keeps `loadRuntimeConfig()`'s never-throws
 * contract on a runtime that has no `import.meta.env`: every read yields
 * undefined and the config resolves blank.
 */
function fromBuildEnv(): RuntimeConfig {
  return {
    apiBaseUrl: trimmed(import.meta.env?.VITE_API_BASE_URL),
    authMode: trimmed(import.meta.env?.VITE_AUTH_MODE),
    githubRepository: trimmed(import.meta.env?.VITE_GITHUB_REPOSITORY),
    msalClientId: trimmed(import.meta.env?.VITE_MSAL_CLIENT_ID),
    msalTenantId: trimmed(import.meta.env?.VITE_MSAL_TENANT_ID),
    apiScope: trimmed(import.meta.env?.VITE_API_SCOPE),
    msalRedirectUri: trimmed(import.meta.env?.VITE_MSAL_REDIRECT_URI),
    msalSilentRedirectUri: trimmed(
      import.meta.env?.VITE_MSAL_SILENT_REDIRECT_URI,
    ),
    faroEnabled: trimmed(import.meta.env?.VITE_FARO_ENABLED) === "true",
    faroCollectorUrl: trimmed(import.meta.env?.VITE_FARO_COLLECTOR_URL),
    environment: trimmed(import.meta.env?.VITE_ENVIRONMENT) || "dev",
    appVersion: trimmed(import.meta.env?.VITE_APP_VERSION) || "dev",
    customerAuthMode: trimmed(import.meta.env?.VITE_CUSTOMER_AUTH_MODE),
    supplierAuthMode: trimmed(import.meta.env?.VITE_SUPPLIER_AUTH_MODE),
    feedbackHubApiBaseUrl: trimmed(
      import.meta.env?.VITE_FEEDBACK_HUB_API_BASE_URL,
    ),
    feedbackHubScope: trimmed(import.meta.env?.VITE_FEEDBACK_HUB_SCOPE),
    objectUiDrafts: trimmed(import.meta.env?.VITE_OBJECT_UI_DRAFTS),
  };
}

/**
 * Overlay a served `config.json` onto the build-time defaults.
 *
 * Only keys present in the document win, so a partially-written file degrades
 * to the build-time value for the rest rather than silently blanking it.
 * Unknown keys are ignored — the file is data written by an operator-facing
 * entrypoint, not a schema we can force them to keep in step.
 */
function overlay(base: RuntimeConfig, raw: unknown): RuntimeConfig {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return base;
  }
  const doc = raw as Record<string, unknown>;
  const merged = { ...base };
  const text = (key: keyof RuntimeConfig): void => {
    const candidate = doc[key];
    if (typeof candidate === "string") {
      (merged[key] as string) = candidate.trim();
    }
  };
  text("apiBaseUrl");
  text("authMode");
  text("githubRepository");
  text("msalClientId");
  text("msalTenantId");
  text("apiScope");
  text("msalRedirectUri");
  text("msalSilentRedirectUri");
  text("faroCollectorUrl");
  text("environment");
  text("appVersion");
  text("customerAuthMode");
  text("supplierAuthMode");
  text("feedbackHubApiBaseUrl");
  text("feedbackHubScope");
  text("objectUiDrafts");

  const faroEnabled = doc.faroEnabled;
  if (typeof faroEnabled === "boolean") {
    merged.faroEnabled = faroEnabled;
  } else if (typeof faroEnabled === "string") {
    merged.faroEnabled = faroEnabled.trim() === "true";
  }
  return merged;
}

/**
 * Resolve configuration for this instance. Idempotent — repeat calls are
 * no-ops once resolved.
 *
 * Must be awaited before anything reads configuration, which in practice means
 * before React mounts. Nothing in `main.tsx`'s *static* import graph may read
 * configuration, or it would evaluate before this resolves.
 *
 * Never throws: a missing, unreachable, or unparseable `config.json` falls back
 * to the build-time environment. That fallback is what keeps local dev,
 * Storybook, Vitest, and the Playwright mock build working untouched.
 */
export async function loadRuntimeConfig(): Promise<void> {
  if (cached !== undefined) return;
  const base = fromBuildEnv();
  try {
    const response = await fetch(RUNTIME_CONFIG_URL, { cache: "no-store" });
    if (!response.ok) {
      cached = base;
      return;
    }
    cached = overlay(base, JSON.parse(await response.text()));
  } catch {
    // 404, offline, non-JSON body, or no server at all — every one of these
    // is a legitimate "no runtime config here" and falls back to the build.
    cached = base;
  }
}

/**
 * The resolved configuration.
 *
 * Throws when `loadRuntimeConfig()` has not completed. That is deliberate: a
 * configuration read that escapes into the pre-boot import graph is a bug that
 * must fail loudly at the callsite rather than silently return build defaults
 * the deployed artifact does not have.
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (cached === undefined) {
    throw new Error(
      "Runtime configuration was read before loadRuntimeConfig() completed. " +
        "Await loadRuntimeConfig() during app bootstrap (see main.tsx in this " +
        "shell, e.g. apps/frontend/src/main.tsx or apps/customer-portal/src/main.tsx) " +
        "before importing or calling anything that reads configuration.",
    );
  }
  return cached;
}
